/* eslint-disable max-len */
import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { Grid, Divider, Typography, Box } from "@mui/material";
import {
  AutoSaveField as Field,
  SelectAutocomplete,
  DateTimePicker
} from "../../../../../../components/Form";
import { useSelector, useDispatch } from "react-redux";
import { getSignatories } from "../../../../../../store/selectors/appConfig";
import { updateRoProspect } from "store/actions/appConfig";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { ButtonWrapper } from "../CompanyBranchesAndSubsidiaries/styled";
import {
  getEidEligibleStakeholders,
  buildInitialValues,
  buildUniqueStakeholderOptions,
  appendEmptyVisit,
  areAllVisitsComplete
} from "./utils";
import {
  getInvalidMessage,
  getMaxDateMessage,
  getMinDateMessage,
  getRequiredMessage
} from "../../../../../../utils/getValidationMessage";
import { ScrollToError } from "../../../../../../components/ScrollToError";
import { FormContainer } from "../CompanyBusinessRelationships/styled";
import { AddButton } from "components/Buttons/AddButton";

const roProspectPath = "roProspect.organizationInfoRO";

const validationSchema = Yup.object().shape({
  siteVisitPostEligibility: Yup.array().of(
    Yup.object().shape({
      signatoryId: Yup.string(),
      eidPingType: Yup.string()
        .nullable()
        .when("signatoryId", {
          is: val => !!val,
          then: Yup.string().required(getRequiredMessage("EID Ping Type")) //NOSONAR
        }),
      eidPingStatus: Yup.string()
        .nullable()
        .when("signatoryId", {
          is: val => !!val,
          then: Yup.string().required(getRequiredMessage("EID Ping Status")) //NOSONAR
        }),
      eidPingDate: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "") return null;
          return value;
        })
        .min(new Date(1900, 0, 1), getMinDateMessage("EID Ping Date")) //NOSONAR
        .max(new Date(), getMaxDateMessage("EID Ping Date")) //NOSONAR
        .typeError(getInvalidMessage("EID Ping Date")) //NOSONAR
        .when("signatoryId", {
          is: val => !!val,
          then: Yup.date() //NOSONAR
            .nullable()
            .transform((value, originalValue) => {
              if (originalValue === "") return null;
              return value;
            })
            .required(getRequiredMessage("EID Ping Date")) //NOSONAR
            .min(new Date(1900, 0, 1), getMinDateMessage("EID Ping Date")) //NOSONAR
            .max(new Date(), getMaxDateMessage("EID Ping Date")) //NOSONAR
            .typeError(getInvalidMessage("EID Ping Date")) //NOSONAR
        })
    })
  )
});

// Extracted component for individual visit form fields
const EidVisitFormFields = ({ visitIndex, uniqueOptions, handleSignatoryIdChange }) => (
  <Grid container spacing={3} mb={2}>
    <Grid item sm={12} xs={12}>
      <Field
        name={`siteVisitPostEligibility[${visitIndex}].signatoryId`}
        path={`${roProspectPath}.siteVisitPostEligibility[${visitIndex}].signatoryId`}
        label="Stakeholder name"
        placeholder="Stakeholder name"
        component={SelectAutocomplete}
        options={uniqueOptions}
        isSearchable
        onChange={handleSignatoryIdChange}
      />
    </Grid>
    <Grid item sm={6} xs={12}>
      <Field
        name={`siteVisitPostEligibility[${visitIndex}].eidPingType`}
        path={`${roProspectPath}.siteVisitPostEligibility[${visitIndex}].eidPingType`}
        label="EID Ping type"
        placeholder="EID Ping type"
        component={SelectAutocomplete}
        datalistId={"eidPingType"}
        isSearchable
      />
    </Grid>
    <Grid item sm={6} xs={12}>
      <Field
        name={`siteVisitPostEligibility[${visitIndex}].eidPingStatus`}
        path={`${roProspectPath}.siteVisitPostEligibility[${visitIndex}].eidPingStatus`}
        label="EID Ping status"
        placeholder="EID Ping status"
        component={SelectAutocomplete}
        datalistId={"eidPingStatus"}
        isSearchable
      />
    </Grid>
    <Grid item sm={12} xs={12}>
      <Field
        name={`siteVisitPostEligibility[${visitIndex}].eidPingDate`}
        path={`${roProspectPath}.siteVisitPostEligibility[${visitIndex}].eidPingDate`}
        label="EID Ping Date"
        maxDate={new Date()}
        component={DateTimePicker}
        storeAsLocalString
      />
    </Grid>
  </Grid>
);

EidVisitFormFields.propTypes = {
  visitIndex: PropTypes.number.isRequired,
  uniqueOptions: PropTypes.array.isRequired,
  handleSignatoryIdChange: PropTypes.func.isRequired
};

export const VisitDetailsEidValidation = ({
  handleContinue,
  createFormChangeHandler,
  existingVisits = []
}) => {
  const dispatch = useDispatch();
  const signatories = useSelector(getSignatories);
  const [showValidationError, setShowValidationError] = React.useState(false);
  const eidEligibleStakeholderOptions = useMemo(() => getEidEligibleStakeholders(signatories), [
    signatories
  ]);
  const maxVisits = eidEligibleStakeholderOptions.length;

  const handleAddVisit = useCallback(
    (siteVisitPostEligibility, setFieldValue) => {
      if (siteVisitPostEligibility.length >= maxVisits) return;
      setFieldValue("siteVisitPostEligibility", appendEmptyVisit(siteVisitPostEligibility));
      setShowValidationError(false);
    },
    [maxVisits]
  );

  const handleSubmit = useCallback(
    (values, formikBag) => {
      const { siteVisitPostEligibility = [] } = values;

      const isAllComplete = areAllVisitsComplete(siteVisitPostEligibility, maxVisits);

      if (isAllComplete) {
        setShowValidationError(false);
        return handleContinue(values, formikBag);
      }

      setShowValidationError(true);
    },
    [handleContinue, maxVisits]
  );

  const handleSignatoryIdChange = useCallback(
    (setFieldValue, visitIndex, value) => {
      setFieldValue(`siteVisitPostEligibility[${visitIndex}].signatoryId`, value);
      const selectedOption = eidEligibleStakeholderOptions.find(opt => opt.value === value);
      if (!selectedOption) {
        setFieldValue(`siteVisitPostEligibility[${visitIndex}].signatoryName`, "");
        setShowValidationError(false);
        return;
      }
      setFieldValue(`siteVisitPostEligibility[${visitIndex}].signatoryName`, selectedOption.label);
      dispatch(
        updateRoProspect({
          [`${roProspectPath}.siteVisitPostEligibility[${visitIndex}].signatoryId`]: value,
          [`${roProspectPath}.siteVisitPostEligibility[${visitIndex}].signatoryName`]: selectedOption.label
        })
      );
      setShowValidationError(false);
    },
    [dispatch, eidEligibleStakeholderOptions]
  );

  const renderVisitForm = useCallback(
    (visit, visitIndex, siteVisitPostEligibility, setFieldValue) => {
      const rowId = visit?.id || `eid-${visitIndex}`;
      const uniqueOptions = buildUniqueStakeholderOptions(
        siteVisitPostEligibility,
        visitIndex,
        eidEligibleStakeholderOptions
      );
      const onSignatoryChange = value => handleSignatoryIdChange(setFieldValue, visitIndex, value);

      return (
        <Box key={rowId}>
          {visitIndex > 0 && <Divider sx={{ my: 3 }} />}
          <EidVisitFormFields
            visitIndex={visitIndex}
            uniqueOptions={uniqueOptions}
            handleSignatoryIdChange={onSignatoryChange}
          />
        </Box>
      );
    },
    [eidEligibleStakeholderOptions, handleSignatoryIdChange]
  );

  return (
    <Formik
      initialValues={buildInitialValues(existingVisits)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={false}
    >
      {createFormChangeHandler(({ values, setFieldValue }) => {
        const { siteVisitPostEligibility = [] } = values;
        const canAddMore = siteVisitPostEligibility.length < maxVisits;
        const isAllComplete = areAllVisitsComplete(siteVisitPostEligibility, maxVisits);
        const showError = maxVisits > 0 && showValidationError && !isAllComplete;

        return (
          <FormContainer>
            <ScrollToError />
            <FieldArray name="siteVisitPostEligibility">
              {() => (
                <>
                  {siteVisitPostEligibility.map((visit, visitIndex) =>
                    renderVisitForm(visit, visitIndex, siteVisitPostEligibility, setFieldValue)
                  )}
                </>
              )}
            </FieldArray>
            {maxVisits > 0 && canAddMore && (
              <AddButton
                onClick={() => handleAddVisit(siteVisitPostEligibility, setFieldValue)}
                disabled={!canAddMore}
                title="Add Another"
                data-testid="add-eid-visit-button"
              />
            )}
            {showError && (
              <Typography color="error" sx={{ mt: 2, mb: 2 }} data-testid="eid-validation-error">
                Please complete EID Ping details for all eligible stakeholders before proceeding.
              </Typography>
            )}
            <ButtonWrapper>
              <ContinueButton type="submit" data-testid="continue-button" />
            </ButtonWrapper>
          </FormContainer>
        );
      })}
    </Formik>
  );
};

VisitDetailsEidValidation.propTypes = {
  handleContinue: PropTypes.func.isRequired,
  createFormChangeHandler: PropTypes.func.isRequired,
  existingVisits: PropTypes.array
};
